import { List } from 'immutable';
import type { AccountSubtype as PlaidAccountSubtypeEnum, AccountType as PlaidAccountTypeEnum } from 'plaid';

import { accountsTypes } from '../accounts';
import type { Account, AccountSubType, AccountType } from '../accounts/accountsTypes';

export type PlaidAccountType = `${PlaidAccountTypeEnum}`;
export type PlaidAccountSubtype = `${PlaidAccountSubtypeEnum}`;

export type PlaidAccount = {
  id: string | null | undefined;
  type?: PlaidAccountType | null;
  subtype?: PlaidAccountSubtype | null;
  name?: string | null;
  mask?: string | null;
};

// Source: "Plaid to QCS Account Type Mapping" - https://quickenteam.atlassian.net/wiki/x/DoCzNAE
const plaidToQCSMap: Partial<
  Record<PlaidAccountType, Partial<Record<PlaidAccountSubtype, { type: AccountType; subType: AccountSubType }>>>
> = {
  depository: {
    'cash management': { type: 'INVESTMENT', subType: 'BROKERAGE' },
    cd: { type: 'BANK', subType: 'CD' },
    checking: { type: 'BANK', subType: 'CHECKING' },
    ebt: { type: 'BANK', subType: 'CHECKING' }, // needs validation
    hsa: { type: 'BANK', subType: 'CHECKING' },
    'limited purpose checking': { type: 'BANK', subType: 'CHECKING' }, // needs validation
    'money market': { type: 'BANK', subType: 'MONEY_MARKET' },
    paypal: { type: 'BANK', subType: 'CHECKING' },
    prepaid: { type: 'BANK', subType: 'CHECKING' },
    savings: { type: 'BANK', subType: 'SAVINGS' },
  },
  credit: {
    'credit card': { type: 'CREDIT', subType: 'CREDIT_CARD' },
    paypal: { type: 'CREDIT', subType: 'CREDIT_CARD' },
  },
  loan: {
    auto: { type: 'LOAN', subType: 'AUTO_LOAN' },
    business: { type: 'LOAN', subType: 'OTHER_LOAN' }, // needs validation
    commercial: { type: 'LOAN', subType: 'COMMERCIAL_LOAN' },
    'commercial line of credit': { type: 'LOAN', subType: 'LINE_OF_CREDIT' },
    construction: { type: 'LOAN', subType: 'CONSTRUCTION_LOAN' },
    consumer: { type: 'LOAN', subType: 'CONSUMER_LOAN' },
    'home equity': { type: 'LOAN', subType: 'HOME_EQUITY' },
    'home equity loan': { type: 'LOAN', subType: 'HOME_EQUITY' }, // needs validation
    'line of credit': { type: 'LOAN', subType: 'LINE_OF_CREDIT' },
    loan: { type: 'LOAN', subType: 'LOAN' },
    mortgage: { type: 'LOAN', subType: 'MORTGAGE' },
    other: { type: 'LOAN', subType: 'OTHER_LOAN' },
    overdraft: { type: 'LOAN', subType: 'OTHER_LOAN' },
    student: { type: 'LOAN', subType: 'STUDENT_LOAN' },
  },
  investment: {
    '529': { type: 'INVESTMENT', subType: '529' },
    '401a': { type: 'INVESTMENT', subType: '401K' },
    '401k': { type: 'INVESTMENT', subType: '401K' },
    '403B': { type: 'INVESTMENT', subType: '403B' },
    '457b': { type: 'INVESTMENT', subType: '401K' },
    brokerage: { type: 'INVESTMENT', subType: 'BROKERAGE' },
    'crypto exchange': { type: 'INVESTMENT', subType: 'OTHER_INVESTMENTS' },
    'education savings account': { type: 'INVESTMENT', subType: 'OTHER_INVESTMENTS' },
    fhsa: { type: 'INVESTMENT', subType: 'OTHER_INVESTMENTS' },
    'fixed annuity': { type: 'INVESTMENT', subType: 'OTHER_INVESTMENTS' },
    gic: { type: 'BANK', subType: 'CD' },
    // 'health reimbursement arrangement': { type: 'INVESTMENT', subType: ??? }, // unmapped subtype; needs follow-up
    hsa: { type: 'INVESTMENT', subType: 'OTHER_INVESTMENTS' },
    ira: { type: 'INVESTMENT', subType: 'IRA' },
    keogh: { type: 'INVESTMENT', subType: 'KEOGH' },
    'line of credit': { type: 'CREDIT', subType: 'CREDIT_CARD' },
    lira: { type: 'INVESTMENT', subType: 'OTHER_INVESTMENTS' },
    lrif: { type: 'INVESTMENT', subType: 'OTHER_INVESTMENTS' },
    lrsp: { type: 'INVESTMENT', subType: 'OTHER_INVESTMENTS' },
    'mutual fund': { type: 'INVESTMENT', subType: 'OTHER_INVESTMENTS' },
    'non-custodial wallet': { type: 'INVESTMENT', subType: 'OTHER_INVESTMENTS' },
    'non-taxable brokerage account': { type: 'INVESTMENT', subType: 'OTHER_INVESTMENTS' },
    other: { type: 'INVESTMENT', subType: 'OTHER_INVESTMENTS' },
    'other annuity': { type: 'INVESTMENT', subType: 'OTHER_INVESTMENTS' },
    pension: { type: 'INVESTMENT', subType: 'OTHER_INVESTMENTS' },
    'prediction market': { type: 'INVESTMENT', subType: 'OTHER_INVESTMENTS' },
    prif: { type: 'INVESTMENT', subType: 'OTHER_INVESTMENTS' },
    'profit sharing plan': { type: 'INVESTMENT', subType: 'OTHER_INVESTMENTS' },
    qshr: { type: 'INVESTMENT', subType: 'OTHER_INVESTMENTS' },
    rdsp: { type: 'INVESTMENT', subType: 'OTHER_INVESTMENTS' },
    resp: { type: 'INVESTMENT', subType: 'OTHER_INVESTMENTS' },
    retirement: { type: 'INVESTMENT', subType: 'IRA' },
    rlif: { type: 'INVESTMENT', subType: 'OTHER_INVESTMENTS' },
    roth: { type: 'INVESTMENT', subType: 'ROTH_IRA' },
    'roth 401k': { type: 'INVESTMENT', subType: 'ROTH_401K' },
    'roth 403B': { type: 'INVESTMENT', subType: 'ROTH_401K' },
    'roth 457b': { type: 'INVESTMENT', subType: 'ROTH_401K' },
    'roth pension': { type: 'INVESTMENT', subType: 'OTHER_INVESTMENTS' },
    'roth profit sharing plan': { type: 'INVESTMENT', subType: 'OTHER_INVESTMENTS' },
    'roth thrift savings plan': { type: 'INVESTMENT', subType: 'OTHER_INVESTMENTS' },
    rrif: { type: 'INVESTMENT', subType: 'OTHER_INVESTMENTS' },
    rrsp: { type: 'INVESTMENT', subType: '401K' },
    sarsep: { type: 'INVESTMENT', subType: 'SEP_IRA' },
    'sep ira': { type: 'INVESTMENT', subType: 'SEP_IRA' },
    'simple ira': { type: 'INVESTMENT', subType: 'SIMPLE_IRA' },
    'stock plan': { type: 'INVESTMENT', subType: 'OTHER_INVESTMENTS' },
    tfsa: { type: 'INVESTMENT', subType: 'ROTH_IRA' },
    'thrift savings plan': { type: 'INVESTMENT', subType: '401K' },
    trust: { type: 'INVESTMENT', subType: 'OTHER_INVESTMENTS' },
    ugma: { type: 'INVESTMENT', subType: 'UGMA_UTMA' },
    utma: { type: 'INVESTMENT', subType: 'UGMA_UTMA' },
    'variable annuity': { type: 'INVESTMENT', subType: 'OTHER_INVESTMENTS' },
  },
  // other: {
  //   other: { type: ???, subType: ??? }, // documented without a subtype
  // },
};

export function transformPlaidAccountTypeSubtypeToQCSTypeSubtype(
  plaidAccountType: PlaidAccountType | null | undefined,
  plaidAccountSubtype: PlaidAccountSubtype | null | undefined,
): { type: AccountType; subType: AccountSubType } {
  const mapped =
    plaidAccountType && plaidAccountSubtype ? plaidToQCSMap[plaidAccountType]?.[plaidAccountSubtype] : undefined;
  return mapped ?? { type: 'UNKNOWN', subType: 'UNKNOWN' };
}

export function transformPlaidAccountTypeToAccountType(
  plaidAccountType: PlaidAccountType | null | undefined,
  plaidAccountSubtype: PlaidAccountSubtype | null | undefined,
): AccountType {
  return transformPlaidAccountTypeSubtypeToQCSTypeSubtype(plaidAccountType, plaidAccountSubtype).type;
}

export function transformPlaidAccountSubTypeToAccountSubType(
  plaidAccountType: PlaidAccountType | null | undefined,
  plaidAccountSubtype: PlaidAccountSubtype | null | undefined,
): AccountSubType {
  return transformPlaidAccountTypeSubtypeToQCSTypeSubtype(plaidAccountType, plaidAccountSubtype).subType;
}

// Web passes no channel when the Plaid environment is unknown.
export function transformPlaidAccountsToAccounts(channel: string | null, accounts: PlaidAccount[]): Account[] {
  return accounts.reduce<Account[]>((list, account) => {
    const type = transformPlaidAccountTypeToAccountType(account.type, account.subtype);
    const subType = transformPlaidAccountSubTypeToAccountSubType(account.type, account.subtype);
    assert(
      type !== 'UNKNOWN' && subType !== 'UNKNOWN',
      `Unhandled plaid account type/subtype: ${account.type}/${account.subtype}`,
      account,
    );

    const aggregatorParams = {
      cpId: account.id,
      channel,

      subType,

      accountName: account.name as string | undefined,
      accountNumberMasked: account.mask as string | undefined,
    };
    const aggregators = List([accountsTypes.mkAccountAggregator(aggregatorParams)]);

    list.push(accountsTypes.mkAccount(type, { aggregators }));
    return list;
  }, []);
}
